// **React Imports
import { memo, useCallback, useEffect, useMemo, useState } from 'react'

// **MUI Imports
import { Box, Typography } from '@mui/material'

// **Component Imports
import CustomDataGrid from '../../../components/data-grid'
import CustomDateTimePicker from '../../../components/date-time-picker'

import { getAICreditUsage } from '../../../axios/api/subscription'
import { FilterChip, FilterPopover } from '../../../components/filter-chip'
import Icon from '../../../components/icon'
import {
  formatDateRangeFilterValue,
  formatDateTime,
  showErrorToast
} from '../../../utils/utils'

const columns = () => [
  {
    field: 'credits',
    display: 'flex',
    headerName: 'Credits',
    flex: 0.3,
    minWidth: 120,
    sortable: true
  },
  {
    field: 'debitedTimestamp',
    display: 'flex',
    headerName: 'Debited at',
    minWidth: 150,
    flex: 0.4,
    sortable: true,
    renderCell: ({ row }) => (
      <>{row?.debitedTimestamp ? formatDateTime(row?.debitedTimestamp) : '-'}</>
    )
  }
]

const AICreditUsageTab = ({
  showTitle = false,
  showBorder = false,
  title = 'Credit Deduction History',
  fromDate = '',
  toDate = ''
}) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState(null)
  const [filters, setFilters] = useState({
    page: 0,
    size: 10,
    sortBy: 'debitedTimestamp',
    sortOrder: 'DESC',
    fromDate: fromDate || '',
    toDate: toDate || ''
  })
  const [dateAnchor, setDateAnchor] = useState(null)

  const pageCount = useMemo(
    () => Math.ceil((data?.totalCount || 0) / filters?.size),
    [filters?.size, data]
  )

  const memoizedColumns = useMemo(() => columns(), [])

  const fetchData = useCallback(async () => {
    setLoading(true)
    const payload = {
      page: filters?.page,
      size: filters?.size,
      sortBy: filters?.sortBy,
      sortOrder: filters?.sortOrder,
      fromDate: filters?.fromDate,
      toDate: filters?.toDate
    }
    const response = await getAICreditUsage(payload)
    setLoading(false)

    if (response?.isError) {
      showErrorToast(response)
      setData(null)
    } else {
      setData(response)
    }
  }, [
    filters?.page,
    filters?.size,
    filters?.sortBy,
    filters?.sortOrder,
    filters?.fromDate,
    filters?.toDate
  ])

  const handleFilterChange = useCallback((key, value) => {
    setFilters((prev) => {
      if (key === 'pageSize') {
        return { ...prev, size: value, page: 0 }
      }
      return { ...prev, [key]: value, ...(key !== 'page' ? { page: 0 } : {}) }
    })
  }, [])

  const handleDateChange = useCallback(
    (key, value) => {
      const updatedValue = value
        ? key === 'toDate'
          ? new Date(new Date(value).setHours(23, 59, 59, 999)).toISOString()
          : value
        : null
      handleFilterChange(key, updatedValue)
    },
    [handleFilterChange]
  )

  const handleSortModelChange = useCallback((sortModel) => {
    const field = sortModel?.[0]?.field || 'debitedTimestamp'
    const direction = sortModel?.[0]?.sort?.toUpperCase() || 'ASC'

    setFilters((prev) => ({
      ...prev,
      sortBy: field,
      sortOrder: direction
    }))
  }, [])

  const hasDateFilter = useMemo(() => {
    return !!(filters?.fromDate || filters?.toDate)
  }, [filters?.fromDate, filters?.toDate])

  const handleClearDateFilter = useCallback(() => {
    setFilters((prev) => ({
      ...prev,
      fromDate: '',
      toDate: '',
      page: 0
    }))
  }, [])

  const dateChipValue = useMemo(() => {
    return formatDateRangeFilterValue(filters?.fromDate, filters?.toDate)
  }, [filters?.fromDate, filters?.toDate])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  return (
    <Box
      sx={{
        border: showBorder ? '1px dashed var(--divider)' : 'none',
        borderRadius: '12px'
      }}
    >
      <Box display="flex" gap={2} justifyContent={'right'} padding={'15px'}>
        <Box
          sx={{
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: showTitle ? 'space-between' : 'flex-end',
            gap: 4
          }}
        >
          {showTitle && (
            <Typography
              variant="h5"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              {title}{' '}
              <Icon
                icon={'mdi:information-outline'}
                style={{ fontSize: '18px', color: 'var(--text-secondary)' }}
                title="This table shows only the credit deductions related to AI token usage."
              />
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <FilterChip
              icon="solar:calendar-date-outline"
              label="Date range"
              value={dateChipValue}
              isActive={!!dateAnchor}
              onClick={(e) => setDateAnchor(e.currentTarget)}
            />
            <FilterPopover
              anchorEl={dateAnchor}
              onClose={() => setDateAnchor(null)}
              title="Filter by Date"
              showClear={hasDateFilter}
              onClear={handleClearDateFilter}
            >
              <Box className="filter-popover-date-body">
                <CustomDateTimePicker
                  name={'fromdate'}
                  label={'From Date'}
                  value={filters?.fromDate || null}
                  onChange={(value) => handleDateChange('fromDate', value)}
                  usedInHookForm={false}
                  fullWidth
                  allowPreviousDate={true}
                  showTimeSelect={false}
                  isClearable
                  onKeyDown={(e) => e.preventDefault()}
                  maxDate={filters?.toDate ? filters?.toDate : new Date()}
                />
                <CustomDateTimePicker
                  label={'To Date'}
                  name={'todate'}
                  value={filters?.toDate || null}
                  onChange={(value) => handleDateChange('toDate', value)}
                  usedInHookForm={false}
                  allowPreviousDate={!filters?.fromDate}
                  showTimeSelect={false}
                  isClearable
                  onKeyDown={(e) => e.preventDefault()}
                  minDate={filters?.fromDate}
                  maxDate={new Date()}
                />
              </Box>
            </FilterPopover>
          </Box>
        </Box>
      </Box>
      <CustomDataGrid
        rows={data?.content || []}
        columns={memoizedColumns || []}
        loading={loading}
        pageCount={pageCount}
        getRowId={(row) => row?.debitedTimestamp}
        pageSizeValue={filters?.size}
        pageValue={filters?.page}
        handlePaginationChange={handleFilterChange}
        rowCount={data?.totalCount || 0}
        paginationMode="server"
        sortingMode="server"
        filterMode="server"
        sortModel={[
          {
            field: filters?.sortBy,
            sort: filters?.sortOrder?.toLowerCase()
          }
        ]}
        onSortModelChange={handleSortModelChange}
      />
    </Box>
  )
}

export default memo(AICreditUsageTab)
